/**
 * Chat-panel footer: hover-corner actions (expand / close for INPUT
 * pill), suggestion chips, input pill with book-a-demo + audio-record +
 * send buttons, privacy disclaimer.
 *
 * Group A leaf 4 of the Preact rewrite — final piece. The textarea
 * input itself is created imperatively by mountWidget (30+ call sites
 * read .value / .focus / addEventListener) and slotted into the
 * component via `inputSlotRef`. Everything else around it is Preact.
 *
 * State (all signals so the surrounding ui.tsx orchestrates):
 *   - hasInputText    drives send vs audio button visibility
 *   - recording       audio button `--recording` class + animated bars
 *   - bookVisible     reveal Book a Demo button when meeting.enabled
 *   - suggestions     SSE-driven chips ABOVE the input pill
 */

import type { Signal } from '@preact/signals';
import type { Ref } from 'preact';
import { SoundBars } from './sound-bars';

export interface FooterBits {
  hasInputText: Signal<boolean>;
  recording: Signal<boolean>;
  bookVisible: Signal<boolean>;
  /**
   * Suggestion chips above the input pill. `label` is shown to the user.
   * Clicking sends `label` as a chat message. The dedicated CTA button
   * lives outside this array (see `cta` below) so it doesn't compete
   * with example prompts for space in the input area.
   */
  suggestions: Signal<Array<{ label: string }>>;
  /**
   * Agent CTA — primary call-to-action ("Schedule a Demo", etc.).
   * Rendered as a dedicated wide button BETWEEN the suggestions and
   * the input wrap so it doesn't crowd the input. Hidden on Engage
   * (deferred to the multi-link chips feature there).
   */
  cta: Signal<{ text: string; link: string } | null>;
  /** Send button disabled while the assistant is streaming. */
  sendDisabled: Signal<boolean>;
  /**
   * M2 (mobile UX program, behind ui.mobileV2): render voice as the
   * start bar's PRIMARY affordance — a labeled ≥44px "Talk" button —
   * instead of the small round audio glyph. True only for phone +
   * flag + voice-capable agents (modes ≥ C); A/B keep send-only.
   */
  talkPrimary?: Signal<boolean>;
  /**
   * True while a realtime voice session is active — flips the Talk
   * button to its End state ("Talk" mid-call is a dead affordance;
   * the same click already toggles the session off).
   */
  voiceActive?: Signal<boolean>;
}

export interface FooterHandlers {
  onCornerExpand: () => void;
  onCornerClose: () => void;
  onSubmit: () => void;
  onBookClick: () => void;
  onAudioClick: () => void;
  onSuggestionClick: (label: string) => void;
}

function FooterIcon({ d, size = 18 }: { d: string; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      dangerouslySetInnerHTML={{ __html: d }}
    />
  );
}

export function Footer({
  bits,
  handlers,
  inputSlotRef,
}: {
  bits: FooterBits;
  handlers: FooterHandlers;
  /** Slot div the imperative textarea gets appended into post-mount. */
  inputSlotRef: Ref<HTMLDivElement>;
}) {
  const hasText = bits.hasInputText.value;
  const recording = bits.recording.value;
  // Read suggestions to keep the signal subscription live — the actual
  // rendering now happens via input.placeholder swap in ui.tsx, but
  // we still want the footer to re-render on suggestion changes so
  // any future chip surface stays reactive without re-wiring.
  void bits.suggestions.value;
  const showBook = bits.bookVisible.value;
  const cta = bits.cta.value;

  return (
    <div class="sp-footer">
      <div class="sp-input-corner-actions">
        <button
          type="button"
          class="sp-input-corner-btn"
          aria-label="Expand chat"
          onClick={handlers.onCornerExpand}
        >
          <FooterIcon
            d='<path d="M15 3h6v6"/><path d="m21 3-7 7"/><path d="m3 21 7-7"/><path d="M9 21H3v-6"/>'
            size={14}
          />
        </button>
        <button
          type="button"
          class="sp-input-corner-btn"
          aria-label="Close chat"
          onClick={handlers.onCornerClose}
        >
          <FooterIcon d='<path d="M18 6L6 18M6 6L18 18"/>' size={14} />
        </button>
      </div>

      {/* Suggestion chips are gone — first suggestion now renders as
        ghost text inside the input pill (see .sp-input-ghost below).
        Visitor presses Enter on an empty input to accept it. Keeps
        the holder element so existing CSS hiding rules don't NPE,
        but always hidden. */}
      <div class="sp-suggestions-holder" style="display:none" />

      {cta ? (
        <button
          type="button"
          class="sp-cta-button"
          onClick={() =>
            window.open(cta.link, '_blank', 'noopener,noreferrer')
          }
        >
          {cta.text}
        </button>
      ) : null}

      <div class="sp-input-wrap">
        {/* Imperative textarea slotted in by mountWidget — keeps the
            existing input.value / focus / addEventListener call sites
            unchanged. The slot div is sp-input-slot; the textarea
            inside it carries .sp-input. The ghost suggestion is
            driven via input.placeholder swap in ui.tsx — see the
            suggestionsSig subscription block there. */}
        <div ref={inputSlotRef} class="sp-input-slot" />
        <div class="sp-actions">
          <button
            type="button"
            class="sp-book-btn"
            style={showBook ? '' : 'display:none'}
            onClick={handlers.onBookClick}
          >
            <FooterIcon
              d='<rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2V6"/><path d="M8 2V6"/><path d="M3 10H21"/>'
              size={16}
            />
            <span>Book a demo</span>
          </button>
          <div class="sp-actions-right">
            {bits.talkPrimary?.value ? (
              /* M2 start bar — Talk is THE single colored button (≥44px,
                 frame 3). Same handler as the audio glyph; the label makes
                 the voice affordance legible at arm's length. Hidden once
                 the visitor types (send takes over). */
              <button
                type="button"
                class={
                  'sp-talk-btn' +
                  (bits.voiceActive?.value
                    ? ' sp-talk-btn--live'
                    : recording
                      ? ' sp-talk-btn--recording'
                      : '')
                }
                aria-label={bits.voiceActive?.value ? 'End the call' : 'Talk to the agent'}
                style={hasText ? 'display:none' : ''}
                onClick={handlers.onAudioClick}
              >
                <SoundBars
                  recording={recording || bits.voiceActive?.value === true}
                  height="14px"
                  barWidth="2.5px"
                  gap="2px"
                />
                <span>{bits.voiceActive?.value ? 'End' : 'Talk'}</span>
              </button>
            ) : (
              <button
                type="button"
                class={
                  'sp-audio-input-btn' +
                  (recording ? ' sp-audio-input-btn--recording' : '')
                }
                aria-label={recording ? 'Stop voice input' : 'Start voice input'}
                style={hasText ? 'display:none' : ''}
                onClick={handlers.onAudioClick}
              >
                <SoundBars
                  recording={recording}
                  height="20px"
                  barWidth="3px"
                  gap="3px"
                />
              </button>
            )}
            <button
              type="button"
              class="sp-send"
              aria-label="Send message"
              style={hasText ? '' : 'display:none'}
              disabled={bits.sendDisabled.value}
              onClick={handlers.onSubmit}
            >
              <FooterIcon
                d='<path d="m5 12 7-7 7 7"/><path d="M12 19V5"/>'
                size={20}
              />
            </button>
          </div>
        </div>
      </div>

      <div class="sp-privacy">
        <p>
          By continuing, you agree this conversation may be recorded.{' '}
          <a
            href="https://www.showpilot.com/privacy"
            target="_blank"
            rel="noopener"
          >
            Privacy Policy
          </a>
        </p>
      </div>
    </div>
  );
}
